import type { ApiClient } from '@daily/contracts/api';
import { ExternalSignInError, externalSignInMessages } from '../session/external-sign-in.js';
import { authErrorOf, codeOf } from './errors.js';
import type { AuthError, AuthSource, FinishProfileInput, SignUpInput } from './types.js';

/** The account calls the v1 screen needs, as the Better Auth browser client answers them. */
export interface AuthRuntime {
  signIn(input: { email: string; password: string }): Promise<unknown>;
  signUp(input: { name: string; email: string; password: string; handle: string; postingTimezone: string }): Promise<unknown>;
  requestPasswordReset(email: string): Promise<unknown>;
  resetPassword(input: { token: string; newPassword: string }): Promise<unknown>;
  resendVerification(email: string): Promise<unknown>;
  signOut(): Promise<unknown>;
  /**
   * Sends the browser to Google once the backend's answer names Google's own sign-in address
   * (`session/auth-gateway.ts`). Rejects only with `ExternalSignInError`.
   */
  signInWithGoogle(): Promise<void>;
}

export interface AuthOptions {
  readonly runtime: AuthRuntime;
  readonly api: ApiClient;
  /** Whether the backend offers Google sign-in (`/api/social/capabilities`). */
  readonly google: boolean;
  /** The member's posting time zone at sign-up (the device's zone). */
  readonly timezone: () => string;
  /** Called after an account step that leaves a different person (or nobody) signed in. */
  readonly changed?: () => void;
}

/**
 * Better Auth answers `{ data, error }`; a failure carries a code (`HANDLE_TAKEN`,
 * `EMAIL_NOT_VERIFIED`, …) and an HTTP status. Rate limits answer 429 without a code.
 */
function check(result: unknown): void {
  if (typeof result !== 'object' || result === null) throw authErrorOf(null, 500);
  const error = (result as { error?: unknown }).error;
  if (!error) return;
  const value = typeof error === 'object' ? error as { code?: unknown; status?: unknown } : {};
  const code = typeof value.code === 'string' && /^[A-Z0-9_]{1,80}$/.test(value.code) ? value.code : null;
  const status = typeof value.status === 'number' ? value.status : 500;
  throw authErrorOf(code, status);
}

/** Any throw on the way (no answer at all) is a network failure; account errors pass through. */
async function step(run: () => Promise<unknown>): Promise<void> {
  let result: unknown;
  try { result = await run(); }
  catch (error) { throw isAuthError(error) ? error : authErrorOf(null, null); }
  check(result);
}

function isAuthError(value: unknown): value is AuthError {
  return !!value && typeof value === 'object' && !(value instanceof Error) && typeof (value as { kind?: unknown }).kind === 'string';
}

/**
 * A Google sign-in that could not start, with the fixed sentence the screen shows for it
 * (`session/external-sign-in.ts`). Nothing of the failure itself is kept: no message, no address.
 */
function googleStartError(failure: unknown): AuthError {
  const reason = failure instanceof ExternalSignInError ? failure.reason : 'startFailed';
  return { kind: reason === 'rateLimited' ? 'too_many' : 'server', detail: `google ${reason}`, text: externalSignInMessages[reason] };
}

export function createV1Auth(options: AuthOptions): AuthSource {
  const { runtime, api } = options;
  const auth: AuthSource = {
    async signIn(email, password) { await step(() => runtime.signIn({ email, password })); options.changed?.(); },
    async signUp(input: SignUpInput) {
      await step(() => runtime.signUp({ name: input.name.trim(), email: input.email.trim(), password: input.password, handle: input.handle.trim().toLowerCase(), postingTimezone: options.timezone() }));
    },
    async resendVerification(email) { await step(() => runtime.resendVerification(email.trim())); },
    async requestPasswordReset(email) { await step(() => runtime.requestPasswordReset(email.trim())); },
    async resetPassword(token, password) { await step(() => runtime.resetPassword({ token, newPassword: password })); options.changed?.(); },
    /** A verified account without a profile (an expired handle claim, Google, an older account) picks its name and handle. */
    async finishProfile(input: FinishProfileInput) {
      try {
        await api.request('saveSocialProfile', { body: { expectedRevision: 0, displayName: input.name.trim(), handle: input.handle.trim().toLowerCase(), postingTimezone: options.timezone() } });
      } catch (error) {
        const code = codeOf(error);
        if (code === 'HANDLE_TAKEN') throw { kind: 'handle_taken', detail: code } satisfies AuthError;
        if (code === 'VALIDATION_FAILED') throw { kind: 'handle_invalid', detail: code } satisfies AuthError;
        if (code === 'REVISION_CONFLICT') return; // the profile exists already: the app loads it
        const status = error && typeof error === 'object' && 'status' in error && typeof error.status === 'number' ? error.status : null;
        throw authErrorOf(code, status);
      }
      options.changed?.();
    },
  };
  // Resolves when the browser has been sent to Google; the session exists only once it comes back.
  if (options.google) auth.signInWithGoogle = async () => {
    try { await runtime.signInWithGoogle(); } catch (failure) { throw googleStartError(failure); }
  };
  return auth;
}

/** The device's time zone, which becomes the member's posting zone at sign-up. */
export function deviceTimezone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}
